import { Injectable, inject, PLATFORM_ID } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { HttpClient } from '@angular/common/http';
import { Observable, of, Subject, interval } from 'rxjs';
import { catchError, takeUntil } from 'rxjs/operators';

import { ANALYTICS_LOGGING_API_CONFIG, AnalyticsLoggingConfig } from '../config';
import { ClientEvent, ClientEventType, EventSeverity, ClientEventResponse } from '../models';

/**
 * Service for logging client-side events and errors
 * 
 * This service provides comprehensive methods for:
 * - Logging client-side errors automatically
 * - Logging warnings and informational events
 * - Tracking performance metrics
 * - Batching events for efficiency (optional)
 * - Preventing duplicate error logging
 * 
 * @remarks
 * - Requires authentication (Firebase JWT)
 * - Events are automatically associated with the authenticated user
 * - Can be used with Angular ErrorHandler for automatic error logging
 * - Supports event batching to reduce network requests
 * 
 * @example
 * ```typescript
 * // Inject the service
 * private errorLogger = inject(ClientErrorLoggerService);
 * 
 * // Log an error
 * this.errorLogger.logError(error, { component: 'MyComponent' }).subscribe();
 * 
 * // Log a warning
 * this.errorLogger.logWarning('Deprecated feature used', { feature: 'oldApi' }).subscribe();
 * ```
 */
@Injectable({
  providedIn: 'root'
})
export class ClientErrorLoggerService {
  private readonly http = inject(HttpClient);
  private readonly config = inject(ANALYTICS_LOGGING_API_CONFIG);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly apiUrl: string;

  // Event batching
  private eventQueue: ClientEvent[] = [];
  private readonly batchSubject = new Subject<void>();
  private lastLoggedError: string = '';
  private lastLoggedTime: number = 0;
  private readonly DUPLICATE_THRESHOLD = 30000; // 30 seconds

  constructor() {
    this.apiUrl = `${this.config.apiBaseUrl}/api/analytics/client-event`;
    this.logDebug('ClientErrorLoggerService initialized', { apiUrl: this.apiUrl });

    // Setup event batching if enabled. Server-side rendering has no real user
    // session to batch events for, and an `interval()` subscription would run
    // forever in the Node process (it's never torn down by a page unload) —
    // so batching only starts in the browser.
    if (this.config.enableBatching && this.isBrowser()) {
      this.setupBatching();
    }
  }

  /**
   * True when running in the browser. Every method below that reads
   * `window`/`navigator` (neither exists in Node) or sends an HTTP event must
   * go through this guard so SSR renders never throw.
   */
  private isBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  /** `window.location.href`, or '' during SSR. */
  private get safeUrl(): string {
    return this.isBrowser() ? window.location.href : '';
  }

  /** `navigator.userAgent`, or '' during SSR. */
  private get safeUserAgent(): string {
    return this.isBrowser() ? navigator.userAgent : '';
  }

  /** `navigator.platform`, or '' during SSR. */
  private get safePlatform(): string {
    return this.isBrowser() ? navigator.platform : '';
  }

  /** `navigator.language`, or '' during SSR. */
  private get safeLanguage(): string {
    return this.isBrowser() ? navigator.language : '';
  }

  // ============================================================================
  // ERROR LOGGING
  // ============================================================================

  /**
   * Log a client-side error
   * 
   * @param error - Error object
   * @param metadata - Additional context (optional)
   * @returns Observable of client event response
   * 
   * @example
   * ```typescript
   * try {
   *   // Some code that might throw
   * } catch (error) {
   *   this.errorLogger.logError(error as Error, {
   *     component: 'BlogListComponent',
   *     action: 'loadPosts',
   *     userId: user.id
   *   }).subscribe();
   * }
   * ```
   */
  logError(error: Error, metadata?: Record<string, any>): Observable<ClientEventResponse> {
    // Check for duplicate errors
    if (this.isDuplicateError(error)) {
      this.logDebug('Skipping duplicate error', { message: error.message });
      return of({ success: true, message: 'Duplicate error skipped' });
    }

    const event: ClientEvent = {
      eventType: ClientEventType.CLIENT_ERROR,
      severity: EventSeverity.ERROR,
      message: error.message || 'Unknown error',
      stackTrace: error.stack,
      orgId: metadata?.['orgId'] as string | undefined,
      metadata: {
        ...metadata,
        errorName: error.name,
        timestamp: new Date().toISOString(),
        userAgent: this.safeUserAgent,
        platform: this.safePlatform,
        language: this.safeLanguage
      },
      url: this.safeUrl,
      userAgent: this.safeUserAgent
    };

    return this.sendEvent(event);
  }

  /**
   * Log a critical error (higher severity than regular error)
   * 
   * @param error - Error object
   * @param metadata - Additional context (optional)
   * @returns Observable of client event response
   * 
   * @example
   * ```typescript
   * this.errorLogger.logCriticalError(error, {
   *   impact: 'Application unusable',
   *   affectedUsers: 'all'
   * }).subscribe();
   * ```
   */
  logCriticalError(error: Error, metadata?: Record<string, any>): Observable<ClientEventResponse> {
    const event: ClientEvent = {
      eventType: ClientEventType.CLIENT_ERROR,
      severity: EventSeverity.CRITICAL,
      message: error.message || 'Critical error',
      stackTrace: error.stack,
      metadata: {
        ...metadata,
        errorName: error.name,
        timestamp: new Date().toISOString()
      },
      url: this.safeUrl,
      userAgent: this.safeUserAgent
    };

    return this.sendEvent(event);
  }

  // ============================================================================
  // WARNING LOGGING
  // ============================================================================

  /**
   * Log a warning event
   * 
   * @param message - Warning message
   * @param metadata - Additional context (optional)
   * @returns Observable of client event response
   * 
   * @example
   * ```typescript
   * this.errorLogger.logWarning('Invalid input detected', {
   *   field: 'email',
   *   value: 'invalid-email',
   *   component: 'LoginForm'
   * }).subscribe();
   * ```
   */
  logWarning(message: string, metadata?: Record<string, any>): Observable<ClientEventResponse> {
    const event: ClientEvent = {
      eventType: ClientEventType.CLIENT_WARNING,
      severity: EventSeverity.WARN,
      message,
      metadata: {
        ...metadata,
        timestamp: new Date().toISOString()
      },
      url: this.safeUrl
    };

    return this.sendEvent(event);
  }

  // ============================================================================
  // INFO LOGGING
  // ============================================================================

  /**
   * Log an informational event
   * 
   * @param message - Info message
   * @param metadata - Additional context (optional)
   * @returns Observable of client event response
   * 
   * @example
   * ```typescript
   * this.errorLogger.logInfo('User completed onboarding', {
   *   userId: user.id,
   *   duration: '5 minutes'
   * }).subscribe();
   * ```
   */
  logInfo(message: string, metadata?: Record<string, any>): Observable<ClientEventResponse> {
    const event: ClientEvent = {
      eventType: ClientEventType.CLIENT_INFO,
      severity: EventSeverity.INFO,
      message,
      metadata: {
        ...metadata,
        timestamp: new Date().toISOString()
      },
      url: this.safeUrl
    };

    return this.sendEvent(event);
  }

  // ============================================================================
  // PERFORMANCE LOGGING
  // ============================================================================

  /**
   * Log a performance metric
   * 
   * @param metric - Metric name
   * @param value - Metric value (in milliseconds)
   * @param metadata - Additional context (optional)
   * @returns Observable of client event response
   * 
   * @example
   * ```typescript
   * const startTime = Date.now();
   * // ... some operation
   * const duration = Date.now() - startTime;
   * 
   * this.errorLogger.logPerformance('page_load', duration, {
   *   component: 'DashboardComponent',
   *   dataSize: posts.length
   * }).subscribe();
   * ```
   */
  logPerformance(metric: string, value: number, metadata?: Record<string, any>): Observable<ClientEventResponse> {
    const event: ClientEvent = {
      eventType: ClientEventType.CLIENT_PERFORMANCE,
      severity: EventSeverity.INFO,
      message: `Performance: ${metric}`,
      metadata: {
        metric,
        value,
        unit: 'ms',
        ...metadata,
        timestamp: new Date().toISOString()
      },
      url: this.safeUrl
    };

    return this.sendEvent(event);
  }

  /**
   * Log slow API call
   * 
   * @param endpoint - API endpoint
   * @param duration - Duration in milliseconds
   * @param method - HTTP method (optional)
   * @returns Observable of client event response
   * 
   * @example
   * ```typescript
   * this.errorLogger.logSlowApiCall('/api/blog-posts', 5000, 'GET').subscribe();
   * ```
   */
  logSlowApiCall(endpoint: string, duration: number, method?: string): Observable<ClientEventResponse> {
    return this.logPerformance('slow_api_call', duration, {
      endpoint,
      method: method || 'UNKNOWN',
      threshold: this.config.performanceThreshold
    });
  }

  // ============================================================================
  // HTTP ERROR LOGGING
  // ============================================================================

  /**
   * Log HTTP error (typically called from interceptor)
   * 
   * @param endpoint - API endpoint
   * @param method - HTTP method
   * @param status - HTTP status code
   * @param error - Error message or object
   * @param duration - Request duration in milliseconds (optional)
   * @returns Observable of client event response
   * 
   * @example
   * ```typescript
   * // In HTTP interceptor
   * this.errorLogger.logHttpError(
   *   req.url,
   *   req.method,
   *   error.status,
   *   error.message,
   *   Date.now() - startTime
   * ).subscribe();
   * ```
   */
  logHttpError(
    endpoint: string,
    method: string,
    status: number,
    error: any,
    duration?: number
  ): Observable<ClientEventResponse> {
    const event: ClientEvent = {
      eventType: ClientEventType.API_ERROR,
      severity: status >= 500 ? EventSeverity.CRITICAL : EventSeverity.WARN,
      message: `HTTP ${status}: ${error.message || 'Request failed'}`,
      metadata: {
        type: 'http',
        endpoint,
        method,
        status,
        statusText: error.statusText,
        duration,
        errorBody: typeof error.error === 'object' ? error.error : error.error,
        timestamp: new Date().toISOString()
      },
      url: this.safeUrl
    };

    return this.sendEvent(event);
  }

  // ============================================================================
  // ROUTE CHANGE LOGGING
  // ============================================================================

  /**
   * Log route/navigation change
   * 
   * @param route - Route URL
   * @param metadata - Additional context (optional)
   * @returns Observable of client event response
   * 
   * @example
   * ```typescript
   * // In app.component.ts
   * this.router.events.pipe(
   *   filter(event => event instanceof NavigationEnd)
   * ).subscribe((event: NavigationEnd) => {
   *   this.errorLogger.logRouteChange(event.urlAfterRedirects).subscribe();
   * });
   * ```
   */
  logRouteChange(route: string, metadata?: Record<string, any>): Observable<ClientEventResponse> {
    return this.logInfo('Route changed', {
      route,
      ...metadata,
      timestamp: new Date().toISOString()
    });
  }

  // ============================================================================
  // EVENT SENDING
  // ============================================================================

  /**
   * Send event to backend
   */
  private sendEvent(event: ClientEvent): Observable<ClientEventResponse> {
    // SSR has no real user session to report events for, and no page-unload
    // hook to flush a batch queue — sending (or even queuing) here would
    // leave the Node process making pointless HTTP calls per render. Skip
    // entirely; browser behavior below is unchanged.
    if (!this.isBrowser()) {
      this.logDebug('Skipping client event on server', { eventType: event.eventType });
      return of({ success: true, message: 'Skipped on server' });
    }

    if (this.config.enableBatching) {
      this.eventQueue.push(event);
      return of({ success: true, message: 'Event queued for batching' });
    }

    this.logDebug('Sending client event', { event });

    return this.http.post<ClientEventResponse>(this.apiUrl, event).pipe(
      catchError(error => {
        console.error('Failed to log client event:', error);
        // Don't crash the app if logging fails
        return of({ success: false, message: 'Failed to log event' });
      })
    );
  }

  /**
   * Flush queued events (for batching)
   */
  flushEvents(): Observable<ClientEventResponse> {
    if (this.eventQueue.length === 0) {
      return of({ success: true, message: 'No events to flush' });
    }

    const events = [...this.eventQueue];
    this.eventQueue = [];

    this.logDebug('Flushing batched events', { count: events.length });

    // Note: Backend would need a batch endpoint to handle multiple events
    // For now, send them individually
    events.forEach(event => {
      this.http.post<ClientEventResponse>(this.apiUrl, event).pipe(
        catchError(error => {
          console.error('Failed to send batched event:', error);
          return of({ success: false, message: 'Failed to send event' });
        })
      ).subscribe();
    });

    return of({ success: true, message: `Flushed ${events.length} events` });
  }

  // ============================================================================
  // UTILITY METHODS
  // ============================================================================

  /**
   * Setup event batching
   */
  private setupBatching(): void {
    const batchInterval = this.config.batchInterval || 30000;
    
    interval(batchInterval)
      .pipe(takeUntil(this.batchSubject))
      .subscribe(() => {
        this.flushEvents().subscribe();
      });

    this.logDebug('Event batching enabled', { interval: batchInterval });
  }

  /**
   * Check if error is a duplicate
   */
  private isDuplicateError(error: Error): boolean {
    const errorKey = `${error.message}-${error.stack}`;
    const now = Date.now();

    if (errorKey === this.lastLoggedError && now - this.lastLoggedTime < this.DUPLICATE_THRESHOLD) {
      return true;
    }

    this.lastLoggedError = errorKey;
    this.lastLoggedTime = now;
    return false;
  }

  /**
   * Debug logging
   */
  private logDebug(message: string, data?: any): void {
    if (this.config.debug) {
      console.log(`[ClientErrorLogger] ${message}`, data || '');
    }
  }

  /**
   * Cleanup on service destroy
   */
  ngOnDestroy(): void {
    this.batchSubject.next();
    this.batchSubject.complete();
    
    // Flush any remaining events
    if (this.config.enableBatching && this.eventQueue.length > 0) {
      this.flushEvents().subscribe();
    }
  }
}
